/* eslint-disable max-depth */
/* eslint-disable max-statements */
/* eslint-disable max-params */
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import {
  describe,
  PropTypes,
  useMemoMergePropsWithDefault,
  useValidateTypescriptPropTypes,
} from '@elliemae/ds-props-helpers';
import {
  useCallbackAfterRender,
  addSpecialCharacters,
  getPartialMaskedPos,
  setCursorPosition,
  flatStringArray,
} from '../utils/index.js';
import { useRegExpMaskDefaultProps, useRegExpMaskPropTypes, type UseRegExpMaskT } from '../react-desc-prop-types.js';
import { useRegExpMaskName } from '../DSMaskDefinitions.js';

const isRegExp = (item: RegExp | string): item is RegExp => typeof item === 'object';

const conformValue = (
  rawValue: string,
  cursorPos: number,
  lastKeyCode: string,
  regExp: (string | RegExp)[],
  filledMask: boolean,
): [string, number] => {
  const specialChars: [string, number][] = [];
  const flatRegExp = flatStringArray(regExp);
  const rawValueArray = String(rawValue).split('');
  let offSet = 0;
  let charsFound = 0;
  let specialCharsLeft = '';
  let fullRegExpMatched = false;
  const maskedValue: string[] = [];
  for (let i = 0; i < flatRegExp.length; i += 1) {
    const currentItem = flatRegExp[i];
    if (isRegExp(currentItem)) {
      specialCharsLeft = '';
      fullRegExpMatched = false;
      for (let j = charsFound + offSet; j < rawValueArray.length; j += 1) {
        const match = rawValueArray[j]?.match(currentItem);
        if (match) {
          maskedValue.push(match[0]);
          charsFound += 1;
          fullRegExpMatched = true;
          break;
        } else {
          offSet += 1;
        }
      }
      if (filledMask && !fullRegExpMatched) {
        maskedValue.push('_');
      }
    } else {
      specialChars.push([currentItem, i]);
      if (rawValueArray[i + offSet] === currentItem) charsFound += 1;
      specialCharsLeft += currentItem;
    }
  }
  if (!fullRegExpMatched) specialCharsLeft = '';

  const maskedPos = getPartialMaskedPos(rawValue, maskedValue, cursorPos);
  const [maskWithSpecialsChars, finalMaskedPos] = addSpecialCharacters(
    maskedValue,
    specialChars,
    maskedPos,
    lastKeyCode,
  );
  const maskReturn = maskWithSpecialsChars + specialCharsLeft;
  return [maskReturn, finalMaskedPos];
};

export const useRegExpMask: ((args: UseRegExpMaskT.Props) => UseRegExpMaskT.OutputT) & { displayName: string } = (
  props,
) => {
  const propsWithDefault = useMemoMergePropsWithDefault<UseRegExpMaskT.InternalProps>(props, useRegExpMaskDefaultProps);
  useValidateTypescriptPropTypes(propsWithDefault, useRegExpMaskPropTypes, useRegExpMaskName);
  const {
    valueSetter,
    onChange: userOnChange,
    onKeyDown: userOnKeyDown,
    onBlur: userOnBlur,
    regExp,
    filledMask,
  } = propsWithDefault;

  const lastKeyCode = useRef('Unidentified');

  const scheduleAfterRender = useCallbackAfterRender(true);

  // Some times, the value of the mask is not gonna change (user types invalid char for example)
  // This makes the input to go from "correct" -> "incorrect" -> "correct"
  // But since the input is controlled, the input does not re-render
  // This makes the input go mumbo-jumbo and places the cursor in the end, because we kinda changed the value async
  // Solution for this is to trigger a re-render, we do this with a set state call in the on change event
  const [, setKey] = useState(0);

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      lastKeyCode.current = e.code;
      if (userOnKeyDown) userOnKeyDown(e);
    },
    [userOnKeyDown],
  );

  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const [mask, cursorPos] = conformValue(
        e.target.value,
        e.target.selectionEnd ?? 0,
        lastKeyCode.current,
        regExp,
        filledMask,
      );

      valueSetter((prevMask) => {
        // Here we need to update the key

        if (mask === prevMask) setKey((key) => key + 1);
        return mask;
      });

      if (userOnChange) userOnChange(e, mask);

      scheduleAfterRender(() => setCursorPosition(e.target, cursorPos));
    },
    [regExp, filledMask, valueSetter, userOnChange, scheduleAfterRender],
  );
  const onBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (filledMask) {
        const maskedValue = e.target.value.split('');

        const flatRegExp = flatStringArray(regExp);
        const firstUnderscoreIdx = maskedValue.findIndex((char, idx) => char === '_' && flatRegExp[idx] !== '_');
        if (firstUnderscoreIdx > -1) valueSetter(maskedValue.slice(0, firstUnderscoreIdx).join(''));
      }

      if (userOnBlur) userOnBlur(e);
    },
    [filledMask, userOnBlur, regExp, valueSetter],
  );

  return {
    onKeyDown,
    onChange,
    onBlur,
  };
};

export const getRegExpMaskedValue = (value: string, regExp: (string | RegExp)[]) => {
  const [maskedValue] = conformValue(value, value.length - 1, '', regExp, false);
  return maskedValue;
};

useRegExpMask.displayName = useRegExpMaskName;
export const UseRegExpMaskWithSchema = describe(useRegExpMask);
UseRegExpMaskWithSchema.propTypes = useRegExpMaskPropTypes;
UseRegExpMaskWithSchema.returnType = {
  onKeyDown: PropTypes.func.description('On key down event for input').isRequired,
  onChange: PropTypes.func.description('On change event for input').isRequired,
  onBlur: PropTypes.func.description('On blur event for input').isRequired,
};
